import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { ToastService } from './toast/toast.service';

const RETURN_TOKEN_KEY = 'impersonateReturnToken';

@Injectable({
  providedIn: 'root',
})
export class ImpersonateService {
  private isImpersonatingSubject = new BehaviorSubject<boolean>(
    !!localStorage.getItem(RETURN_TOKEN_KEY),
  );
  isImpersonating$: Observable<boolean> =
    this.isImpersonatingSubject.asObservable();

  constructor(private toastService: ToastService, private router: Router) {}

  /**
   * Get the return token from localStorage
   */
  getReturnToken(): string | null {
    return localStorage.getItem(RETURN_TOKEN_KEY);
  }

  /**
   * Impersonate a user by calling the Meteor method and storing the return token
   */
  impersonateUser(targetUserId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Meteor.call(
        'impersonateUser',
        targetUserId,
        (
          error: Meteor.Error,
          result: { token: string; returnToken: string },
        ) => {
          if (error) {
            this.toastService.add({ type: 'ERROR', message: 'serverError' });
            reject(error);
            return;
          }
          if (result && result.token && result.returnToken) {
            // Store the return token in localStorage
            localStorage.setItem(RETURN_TOKEN_KEY, result.returnToken);
            // Log in with the impersonation token
            Meteor.loginWithToken(result.token, (loginError) => {
              if (loginError) {
                // Clear the return token if login fails
                localStorage.removeItem(RETURN_TOKEN_KEY);
                if (
                  loginError instanceof Meteor.Error &&
                  loginError.reason === 'demoExpired'
                ) {
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'demoExpired',
                  });
                } else {
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                }
                reject(loginError);
                return;
              }
              // Reload the page to ensure all subscriptions and state are refreshed
              window.location.href = '/dashboard';
              resolve();
            });
          } else {
            reject(new Error('Invalid response from server'));
          }
        },
      );
    });
  }

  /**
   * Return to the original user by using the stored return token
   */
  returnToOriginalUser(): Promise<void> {
    const returnToken = this.getReturnToken();
    if (!returnToken) {
      return Promise.reject(new Error('No return token found'));
    }

    return new Promise((resolve, reject) => {
      // Clear the return token from localStorage before applying it
      localStorage.removeItem(RETURN_TOKEN_KEY);
      // Log in with the return token
      Meteor.loginWithToken(returnToken, (loginError) => {
        if (loginError) {
          if (
            loginError instanceof Meteor.Error &&
            loginError.reason === 'demoExpired'
          ) {
            this.toastService.add({
              type: 'ERROR',
              message: 'demoExpired',
            });
          } else {
            this.toastService.add({
              type: 'ERROR',
              message: 'serverError',
            });
          }
          reject(loginError);
          return;
        }
        // Reload the page to ensure all subscriptions and state are refreshed
        window.location.href = '/dashboard';
        resolve();
      });
    });
  }
}
